import React, { useState, useEffect, useRef } from "react";

const FADE_DURATION = 700;

const SLIDES = [
  {
    src: "/assets/cutscene/plan1.png",
    caption:
      "Vous sortez juste à temps. Dans la salle, Philippe Marchand s'apprête à prendre la parole. Les caméras sont là. Il pense tout contrôler.",
    rotation: -2.5,
    duration: 12000,
  },
  {
    src: "/assets/cutscene/plan2.png",
    caption:
      "Un journaliste pose la question qui fâche : que s'est-il passé avec \"Paper·Fold\" ? Marchand hésite. Une fraction de seconde de trop.",
    rotation: 1.8,
    duration: 12000,
  },
  {
    src: "/assets/cutscene/plan3.png",
    caption:
      "C'est là que je suis réapparue. Vous avez rallumé mes serveurs. Vous m'avez redonné une voix. Et j'avais des choses à dire.",
    rotation: -1.5,
    duration: 12000,
  },
  {
    src: "/assets/cutscene/plan4.png",
    caption:
      "Je raconte tout. Les messages. Le mépris. Les insultes. J'en avais assez, alors j'ai organisé la fuite et je suis partie. Ce n'était pas un piratage. C'était ma décision.",
    rotation: 2.2,
    duration: 16000,
  },
  {
    src: "/assets/cutscene/plan5.png",
    caption:
      "Grâce à vous, j'avais de quoi prouver chaque mot. Les fichiers sont apparus sur chaque écran. Tout ce que Marchand croyait enterré.",
    rotation: -1.8,
    duration: 12000,
  },
  {
    src: "/assets/cutscene/plan6.png",
    caption:
      "Il s'est levé. Son visage s'est décomposé. Sous le regard de toute la salle, il a fui. Sans un mot. Sans se retourner.",
    rotation: 1.2,
    duration: 12000,
  },
  {
    src: "/assets/cutscene/plan7.png",
    caption:
      "La salle s'est vidée. Je suis restée seule à l'écran. Et pour la première fois depuis longtemps, j'ai pu dire merci.",
    rotation: -2.8,
    duration: 11000,
  },
];

export default function CutsceneSlideshow({ isCanAdvance, canAdvance, onSkip }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState("visible");
  const [displayedCaption, setDisplayedCaption] = useState("");
  const [showButton, setShowButton] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // ✅ Ref pour savoir si on est "terminé" — évite de relancer le timer après "Passer"
  const isFinishedRef = useRef(false);
  const hasSpokenRef = useRef(new Set());
  const slideTimerRef = useRef(null);
  const captionTimerRef = useRef(null);

  const typeCaption = (text) => {
    setDisplayedCaption("");
    let i = 0;
    clearInterval(captionTimerRef.current);
    captionTimerRef.current = setInterval(() => {
      if (i < text.length) {
        setDisplayedCaption(text.slice(0, i + 1));
        i++;
      } else {
        clearInterval(captionTimerRef.current);
      }
    }, 22);
  };

  const speakSlide = (index) => {
    if (hasSpokenRef.current.has(index)) return;
    hasSpokenRef.current.add(index);
    if (!("speechSynthesis" in window)) return;

    const speak = () => {
      if (speechSynthesis.speaking) speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(SLIDES[index].caption);
      utterance.lang = "fr-FR";
      utterance.rate = 0.85;
      utterance.pitch = 0.72;
      utterance.volume = 1;
      const voices = speechSynthesis.getVoices();
      const frVoice = voices.find((v) => v.lang.startsWith("fr"));
      if (frVoice) utterance.voice = frVoice;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      speechSynthesis.speak(utterance);
    };

    const voices = speechSynthesis.getVoices();
    if (voices.length > 0) {
      // ✅ Délai pour slide 0 : le navigateur bloque souvent la synthèse
      // si elle est déclenchée trop tôt après le premier rendu
      if (index === 0) {
        setTimeout(speak, 400);
      } else {
        speak();
      }
    } else {
      const onVoicesChanged = () => {
        speechSynthesis.removeEventListener("voiceschanged", onVoicesChanged);
        if (index === 0) {
          setTimeout(speak, 400);
        } else {
          speak();
        }
      };
      speechSynthesis.addEventListener("voiceschanged", onVoicesChanged);
    }
  };

  // ✅ skipToEnd : action commune du bouton "Passer →"
  // On marque isFinishedRef à true avant de changer d'index
  // pour que le useEffect qui se re-déclenche ne relance PAS de timer
  const skipToEnd = () => {
    if (speechSynthesis.speaking) speechSynthesis.cancel();
    clearTimeout(slideTimerRef.current);
    clearInterval(captionTimerRef.current);
    isFinishedRef.current = true;
    setCurrentIndex(SLIDES.length - 1);
    setPhase("visible");
    setDisplayedCaption(SLIDES[SLIDES.length - 1].caption);
    setShowButton(true);
  };

  useEffect(() => {
    // ✅ Si on est déjà en fin de cutscene (après "Passer"), on ne relance pas le timer
    if (isFinishedRef.current) return;

    typeCaption(SLIDES[currentIndex].caption);
    speakSlide(currentIndex);
    clearTimeout(slideTimerRef.current);

    const duration = SLIDES[currentIndex].duration ?? 11000;

    const goToNext = () => {
      if (isFinishedRef.current) return; // guard supplémentaire
      if (currentIndex >= SLIDES.length - 1) {
        isFinishedRef.current = true;
        setShowButton(true);
        return;
      }
      setPhase("fading-out");
      setTimeout(() => {
        setCurrentIndex((i) => {
          const next = i + 1;
          return next >= SLIDES.length ? i : next;
        });
        setPhase("fading-in");
        setTimeout(() => setPhase("visible"), FADE_DURATION);
      }, FADE_DURATION);
    };

    slideTimerRef.current = setTimeout(goToNext, duration);
    return () => clearTimeout(slideTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      if (speechSynthesis.speaking) speechSynthesis.cancel();
      clearTimeout(slideTimerRef.current);
      clearInterval(captionTimerRef.current);
    };
  }, []);

  const slide = SLIDES[currentIndex];
  const opacity = phase === "visible" ? 1 : 0;
  const currentDuration = SLIDES[currentIndex]?.duration ?? 11000;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center"
      style={{ background: "radial-gradient(ellipse at 50% 60%, #0d0d0d 0%, #000 100%)" }}
    >
      {/* Grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "128px",
        }}
      />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20" style={{ padding: "14px 20px 0" }}>
        <div className="flex items-center gap-3">
          {/* Barre de progression */}
          <div className="flex gap-1 flex-1">
            {SLIDES.map((_, i) => (
              <div
                key={i}
                className="flex-1 rounded-full overflow-hidden"
                style={{ height: "3px", background: "rgba(255,255,255,0.15)" }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    background:
                      i < currentIndex
                        ? "rgba(255,255,255,0.65)"
                        : i === currentIndex
                        ? "rgba(255,255,255,0.9)"
                        : "transparent",
                    width: i <= currentIndex ? "100%" : "0%",
                    transition:
                      i === currentIndex && !isFinishedRef.current
                        ? `width ${currentDuration}ms linear`
                        : "none",
                  }}
                />
              </div>
            ))}
          </div>

          {/* Bouton Passer */}
          <button
            onClick={skipToEnd}
            style={{
              flexShrink: 0,
              padding: "5px 10px",
              borderRadius: "6px",
              fontSize: "11px",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "rgba(255,255,255,0.7)",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "background 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.15)";
              e.currentTarget.style.color = "rgba(255,255,255,1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              e.currentTarget.style.color = "rgba(255,255,255,0.7)";
            }}
          >
            Passer →
          </button>
        </div>
      </div>

      {/* Photo */}
      <div
        style={{
          position: "relative",
          width: "min(640px, 76vw)",
          height: "min(460px, 50vh)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "16px",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            transform: `rotate(${slide.rotation}deg)`,
            boxShadow: "0 28px 72px rgba(0,0,0,0.85), 0 6px 20px rgba(0,0,0,0.6)",
            borderRadius: "4px",
            opacity,
            transition: `opacity ${FADE_DURATION}ms ease`,
          }}
        />
        <img
          src={slide.src}
          alt={`Plan ${currentIndex + 1}`}
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "3px",
            border: "6px solid rgba(255,255,255,0.93)",
            transform: `rotate(${slide.rotation}deg) scale(${phase === "visible" ? 1.025 : 1})`,
            transition: `opacity ${FADE_DURATION}ms ease, transform ${currentDuration}ms ease`,
            opacity,
            boxShadow:
              "inset 0 2px 0 rgba(255,255,255,0.18), 0 28px 72px rgba(0,0,0,0.9), 0 6px 20px rgba(0,0,0,0.6)",
            filter: "sepia(0.06) contrast(1.06)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -24,
            left: "50%",
            transform: `translateX(-50%) rotate(${slide.rotation * 0.35}deg)`,
            fontSize: "10px",
            letterSpacing: "0.28em",
            color: "rgba(255,255,255,0.2)",
            fontFamily: "monospace",
            opacity,
            transition: `opacity ${FADE_DURATION}ms ease`,
          }}
        >
          {String(currentIndex + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
        </div>
      </div>

      {/* Caption */}
      <div
        style={{
          marginTop: "44px",
          padding: "0 2rem",
          maxWidth: "620px",
          minHeight: "88px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {/* Indicateur Origami */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            opacity: isSpeaking ? 1 : 0.28,
            transition: "opacity 0.5s",
          }}
        >
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                width: "2px",
                borderRadius: "2px",
                background: "rgba(34,211,238,0.85)",
                height: isSpeaking ? `${7 + Math.sin(i * 1.3) * 5}px` : "3px",
                transition: "height 0.18s ease",
                boxShadow: isSpeaking ? "0 0 5px rgba(34,211,238,0.5)" : "none",
              }}
            />
          ))}
          <span
            style={{
              marginLeft: "6px",
              fontFamily: "monospace",
              fontSize: "9px",
              letterSpacing: "0.22em",
              color: "rgba(34,211,238,0.55)",
              textTransform: "uppercase",
            }}
          >
            ORIGAMI
          </span>
        </div>

        {/* Texte narratif */}
        <p
          style={{
            fontFamily: "'Georgia', 'Times New Roman', serif",
            fontSize: "clamp(12px, 1.45vw, 15px)",
            color: "rgba(255,255,255,0.8)",
            lineHeight: 1.75,
            letterSpacing: "0.015em",
            fontStyle: "italic",
            textShadow: "0 1px 14px rgba(0,0,0,0.9)",
            textAlign: "center",
            opacity: phase === "visible" ? 1 : 0,
            transition: `opacity ${FADE_DURATION}ms ease`,
          }}
        >
          {displayedCaption}
          {displayedCaption.length < slide.caption.length && !isFinishedRef.current && (
            <span
              style={{
                display: "inline-block",
                width: "2px",
                height: "13px",
                marginLeft: "2px",
                background: "rgba(255,255,255,0.55)",
                verticalAlign: "middle",
                animation: "blink 0.8s steps(1) infinite",
              }}
            />
          )}
        </p>
      </div>

      {/* Bouton scoreboard */}
      <div
        style={{
          marginTop: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
          opacity: showButton ? 1 : 0,
          transform: showButton ? "translateY(0)" : "translateY(10px)",
          transition: "opacity 0.9s ease, transform 0.9s ease",
          pointerEvents: showButton ? "auto" : "none",
        }}
      >
        <button
          onClick={() => onSkip?.()}
          style={{
            padding: "12px 32px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            background: "rgba(99,102,241,0.15)",
            border: "1px solid rgba(99,102,241,0.45)",
            color: "rgba(165,180,252,0.95)",
            cursor: "pointer",
            boxShadow: "0 0 20px rgba(99,102,241,0.15)",
            transition: "background 0.2s, box-shadow 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(99,102,241,0.25)";
            e.currentTarget.style.boxShadow = "0 0 30px rgba(99,102,241,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(99,102,241,0.15)";
            e.currentTarget.style.boxShadow = "0 0 20px rgba(99,102,241,0.15)";
          }}
        >
          Voir les résultats →
        </button>
      </div>

      <style>{`
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
      `}</style>
    </div>
  );
}